import React from 'react'

import {
  Box,
  Display,
  DisplayPosition,
  FontWeight,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { DietaryWarningTag } from '../Tags'

type RecipeTagColor = {
  light: string
  dark?: string
}

export type RecipeDietaryOverlayTag = {
  type: string
  text: string
}

export type RecipeTagLabel = {
  type: string
  text: string
  color?: RecipeTagColor
  backgroundColor?: RecipeTagColor
}

const DietaryBadge = withCitrusTheme(Box, (theme) => ({
  width: '28px',
  height: '28px',
  borderRadius: '999px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.tokens.colors.ColdGrey_700,
  color: theme.tokens.colors.White,
}))

const LabelPill = withCitrusTheme(Box, () => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '24px',
  minWidth: 0,
  maxWidth: '100%',
  padding: '0 0.5rem',
}))

export const RecipeDietaryTagOverlay = ({
  tags,
  dataTestId = 'recipe-tile-dietary-tags',
}: {
  tags: RecipeDietaryOverlayTag[]
  dataTestId?: string
}) => {
  if (tags.length === 0) {
    return null
  }

  return (
    <Box
      data-testid={dataTestId}
      display={Display.Flex}
      gap={1}
      position={DisplayPosition.Absolute}
      style={{
        top: '1rem',
        right: '1rem',
        zIndex: 2,
      }}
    >
      {tags.map((tag) => (
        <DietaryBadge key={tag.type} style={{ opacity: 0.9 }}>
          <Text size={2} fontWeight={FontWeight.Bold} style={{ color: 'inherit', lineHeight: '1' }}>
            {tag.text}
          </Text>
        </DietaryBadge>
      ))}
    </Box>
  )
}

export const RecipeTagLabels = ({
  tags,
  isOutOfStock = false,
  reserveEndSpace = false,
  dataTestId = 'recipe-tile-general-labels',
  style,
}: {
  tags: RecipeTagLabel[]
  isOutOfStock?: boolean
  reserveEndSpace?: boolean
  dataTestId?: string
  style?: React.CSSProperties
}) => {
  if (!isOutOfStock && tags.length === 0) {
    return null
  }

  const wrapperStyle: React.CSSProperties = {
    position: DisplayPosition.Absolute,
    top: '1rem',
    left: 0,
    zIndex: 12,
    minWidth: 0,
    maxWidth: reserveEndSpace ? 'calc(100% - 88px)' : 'calc(100% - 2rem)',
    overflow: 'hidden',
    ...style,
  }

  if (isOutOfStock) {
    return (
      <Box data-testid="sold-out-tag" style={wrapperStyle}>
        <LabelPill
          style={{
            backgroundColor: '#4F5C68',
            borderRadius: '0 4px 4px 0',
            opacity: 0.9,
            color: '#FFFFFF',
          }}
        >
          <Text
            size={2}
            fontWeight={FontWeight.Bold}
            style={{ color: 'inherit', lineHeight: '24px' }}
          >
            Sold Out
          </Text>
        </LabelPill>
      </Box>
    )
  }

  return (
    <Box
      data-testid={dataTestId}
      display={Display.Flex}
      gap={2}
      style={{
        flexDirection: 'column',
        alignItems: 'flex-start',
        ...wrapperStyle,
      }}
    >
      {tags.map((label) => (
        <RecipeTagLabelPill key={`${label.type}-${label.text}`} label={label} />
      ))}
    </Box>
  )
}

const RecipeTagLabelPill = ({ label }: { label: RecipeTagLabel }) => {
  if (label.type === 'invalidPreference') {
    return <DietaryWarningTag text={label.text} variant="stacked" />
  }

  if (!label.color || !label.backgroundColor) {
    return null
  }

  return (
    <LabelPill
      style={{
        backgroundColor: label.backgroundColor.light,
        borderRadius: '0 4px 4px 0',
        opacity: 0.9,
        color: label.color.light,
      }}
    >
      <Text
        size={2}
        fontWeight={FontWeight.Bold}
        style={{
          color: 'inherit',
          lineHeight: '24px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
        }}
      >
        {label.text}
      </Text>
    </LabelPill>
  )
}
